import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Archive, Instagram, MapPin, Calendar } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import PageHeader, { SectionMark } from "@/components/PageHeader";
import SEO from "@/components/SEO";
import EventCard from "@/components/events/EventCard";
import ShareEventButton from "@/components/events/ShareEventButton";
import { useEvents } from "@/components/events/NextEvents";
import { splitEvents, fmtEventDates, pick } from "@/components/events/eventUtils";
import EventCountdown from "@/components/events/EventCountdown";

function FeaturedHero({ ev, lang }) {
    const en = lang === "en";
    const to = `/eventi/${ev.slug}`;
    return (
        <section id="featured" data-testid="eventi-featured" className="border-b border-white/10 scroll-mt-16">
            <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-16">
                <SectionMark n="01" title={en ? "Featured event" : "In primo piano"}
                    right={<EventCountdown ev={ev} lang={lang} compact testid="eventi-featured-countdown" />} />
                <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">
                    <Link to={to} className="lg:col-span-5 lg:order-2 block border-2 border-abnormal-paper/80 bg-black" aria-label={pick(ev, "title", lang)}>
                        {ev.poster && <img src={ev.poster} alt={pick(ev, "title", lang)} loading="eager" decoding="async" className="w-full h-auto block" />}
                    </Link>
                    <div className="lg:col-span-7 lg:order-1">
                        {ev.series && <p className="label-mono text-abnormal-magenta">{ev.series}</p>}
                        <h2 className="mt-3 font-display uppercase leading-[0.85] tracking-tighter text-abnormal-paper text-4xl sm:text-5xl md:text-7xl">
                            {pick(ev, "title", lang)}<span className="text-abnormal-acid">.</span>
                        </h2>
                        <p className="mt-4 flex flex-wrap gap-x-5 gap-y-1.5 font-display uppercase text-abnormal-paper text-lg md:text-2xl leading-none tracking-tight">
                            <span className="inline-flex items-center gap-2"><Calendar size={16} className="text-abnormal-acid" /> {fmtEventDates(ev, lang)}</span>
                            <span className="inline-flex items-center gap-2 text-abnormal-paperDim"><MapPin size={16} className="text-abnormal-acid" /> {ev.location}</span>
                        </p>
                        {pick(ev, "tagline", lang) && <p className="mt-6 font-display uppercase leading-[0.9] tracking-tight text-abnormal-acid misprint text-3xl sm:text-4xl md:text-5xl">{pick(ev, "tagline", lang)}</p>}
                        <p className="mt-4 text-abnormal-paperDim text-base md:text-lg leading-relaxed max-w-xl">{pick(ev, "summary", lang)}</p>
                        {pick(ev, "highlight", lang) && <p className="mt-4 label-mono text-abnormal-magenta">{pick(ev, "highlight", lang)}</p>}
                        <div className="mt-6 flex flex-wrap items-center gap-3">
                            <Link to={to} data-testid="eventi-featured-view" className="btn-primary">VIEW EVENT <ArrowRight size={14} /></Link>
                            {ev.registration_url && (
                                <a href={ev.registration_url} target="_blank" rel="noreferrer" data-testid="eventi-featured-book"
                                    className="inline-flex items-center gap-2 bg-abnormal-magenta text-black hover:bg-abnormal-paper px-6 py-3 text-xs uppercase tracking-[0.2em] font-bold transition-colors">
                                    {pick(ev, "registration_label", lang) || "BOOK NOW"} <ArrowUpRight size={13} />
                                </a>
                            )}
                            <ShareEventButton event={ev} />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default function Eventi() {
    const { lang } = useLang();
    const en = lang === "en";
    const { hash } = useLocation();
    const events = useEvents();
    useEffect(() => {
        if (!hash || events === null) return;
        const go = () => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth", block: "start" });
        const tms = [120, 700, 1600].map((ms) => setTimeout(go, ms));
        return () => tms.forEach(clearTimeout);
    }, [hash, events]);

    const { upcoming, past, featured } = splitEvents(events || []);
    const others = upcoming.slice(1);

    return (
        <div data-testid="eventi-page" className="bg-abnormal-ink">
            <SEO
                title={en ? "Events · Races, camps & ABNORMAL collaborations" : "Eventi · Gare, corsi e collaborazioni ABNORMAL"}
                description={en
                    ? "Upcoming events and archive: Tuscany Urban Cup, Toscano Enduro Series, E-Bike Enduro camps on Monte Argentario. ABNORMAL inside the MTB, Enduro and Downhill scene."
                    : "Prossimi eventi e archivio: Tuscany Urban Cup, Toscano Enduro Series, corsi E-Bike Enduro al Monte Argentario. ABNORMAL nel mondo MTB, Enduro e Downhill."}
                image={featured?.poster}
                path="/eventi"
            />
            <PageHeader
                label="EVENTS"
                title={en ? "Races, camps" : "Gare, corsi"}
                accent={en ? "& collaborations" : "& collaborazioni"}
                tagline={["Enduro", "Downhill", "Toscana"]}
                sub={en
                    ? "Where ABNORMAL shows up for real: races, camps and events on the trails. Upcoming dates first, then the permanent archive."
                    : "Dove ABNORMAL c'è davvero: gare, corsi ed eventi sui trail. Prima le prossime date, poi l'archivio permanente."}
                image="https://images.pexels.com/photos/12778081/pexels-photo-12778081.jpeg"
                imageAlt="Eventi MTB Enduro"
            />

            {featured && <FeaturedHero ev={featured} lang={lang} />}

            <section id="upcoming" data-testid="eventi-upcoming" className="border-b border-white/10 scroll-mt-16">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-16">
                    <SectionMark n={featured ? "02" : "01"} title={en ? "Upcoming events · Prossimi eventi" : "Upcoming events · Prossimi eventi"}
                        right={<span className="label-mono text-abnormal-paperDim/60">{upcoming.length} {en ? "in programme" : "in programma"}</span>} />
                    {events === null ? (
                        <div className="h-40" />
                    ) : upcoming.length === 0 ? (
                        <div data-testid="eventi-no-upcoming" className="border border-white/15 bg-abnormal-charcoal p-8 md:p-12 halftone">
                            <p className="font-display uppercase leading-[0.9] tracking-tight text-abnormal-paper text-3xl sm:text-5xl md:text-6xl">The next ride is coming.<br /><span className="text-abnormal-acid">Stay Abnormal.</span></p>
                        </div>
                    ) : (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                            {upcoming.map((ev, i) => <EventCard key={ev.slug} event={ev} index={i} />)}
                        </div>
                    )}
                    {others.length === 0 && featured && (
                        <p className="mt-4 label-mono text-abnormal-paperDim/60">{en ? "More dates coming soon." : "Altre date in arrivo."}</p>
                    )}
                </div>
            </section>

            <section id="past" data-testid="eventi-past" className="scroll-mt-16">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-16">
                    <SectionMark n={featured ? "03" : "02"} title={en ? "Past events · Archive" : "Past events · Archivio"}
                        right={<span className="label-mono text-abnormal-paperDim/60 inline-flex items-center gap-1.5"><Archive size={11} /> {past.length}</span>} />
                    {past.length === 0 ? (
                        <p className="text-abnormal-paperDim label-mono">{en ? "Nothing in the archive yet." : "Archivio ancora vuoto."}</p>
                    ) : (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                            {past.map((ev) => <EventCard key={ev.slug} event={ev} />)}
                        </div>
                    )}
                </div>
            </section>

            <section className="border-t border-white/10">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-16 grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-7 space-y-5 text-abnormal-paperDim">
                        <p className="font-display uppercase text-3xl md:text-4xl leading-tight tracking-tight text-abnormal-paper">
                            {en ? "ABNORMAL is official sponsor of the " : "ABNORMAL è sponsor ufficiale della "}<span className="text-abnormal-acid">Tuscany Urban Cup</span>{en ? ", the MTB race series mixing Enduro with Tuscany's urban soul." : ", il circuito di gare MTB che unisce l'Enduro con l'anima urban della Toscana."}
                        </p>
                        <a href="https://www.instagram.com/tuscanyurbancup/" target="_blank" rel="noreferrer" data-testid="tuscany-ig-cta" className="btn-primary">
                            <Instagram size={16} /> @tuscanyurbancup <ArrowUpRight size={13} />
                        </a>
                    </div>
                    <aside className="lg:col-span-4 lg:col-start-9 border border-white/15 p-6 self-start topo-lines">
                        <p className="label-mono text-abnormal-acid">// {en ? "Field notes" : "Note di campo"}</p>
                        <ul className="mt-4 space-y-3 text-sm text-abnormal-paperDim">
                            <li className="flex gap-3"><span className="text-abnormal-acid font-vt">01</span>{en ? "Every event keeps its page in the archive, forever." : "Ogni evento conserva la sua pagina nell'archivio, per sempre."}</li>
                            <li className="flex gap-3"><span className="text-abnormal-acid font-vt">02</span>{en ? "Local rides every week: check Ride Call." : "Giri locali ogni settimana: guarda Ride Call."}</li>
                            <li className="flex gap-3"><span className="text-abnormal-acid font-vt">03</span>{en ? "Trail status before you come: Trail Report." : "Stato dei trail prima di venire: Trail Report."}</li>
                        </ul>
                    </aside>
                </div>
            </section>
        </div>
    );
}
